import { format } from "date-fns";
import { useTranslations } from "next-intl";
import { useCallback, useMemo } from "react";

import { DateRange, DateRangePicker } from "@zapier/design-system";

import { getDateOptions } from "@zapier/reporting/src/components/pages/HistoryShared/getDateOptions";
import { SizeType, useAccountAndUser } from "@zapier/reporting/src/hooks";
import { DateFilterType } from "@zapier/reporting/src/types";
import { omitBy } from "@zapier/reporting/src/utils";

type DateFilterProps = {
  id?: string;
  onChange: (item: Partial<DateFilterType>) => void;
  size: SizeType;
  value: DateFilterType | undefined;
};

/**
 * This component is different from the DateFilter that is used for `HistoryPage`
 * It originally was the same component in both the embeddable RunLog and HistoryPage,
 * but we updated HistoryPage's filters to multi-select, and the UI changed for how it
 * represents the values on the page. Multi-select renders pills under the filters for each
 * distinct filter value, while the embed needs to render the date within the input
 * itself (like it originally did)
 */
export const EmbedDateFilter = (props: DateFilterProps) => {
  const t = useTranslations("run-log.embed-date-filter");
  const { onChange } = props;

  const { currentUsageCycle } = useAccountAndUser();

  const dateOptions = useMemo(
    () =>
      getDateOptions(
        currentUsageCycle
          ? {
              from: new Date(currentUsageCycle.from),
              to: new Date(currentUsageCycle.to),
            }
          : {}
      ),
    [currentUsageCycle]
  );

  const handleChange = useCallback(
    (value: DateRange) => {
      const { from, to } = value;

      // The date picker does not pick time, so filter that out
      const fromStart = from ? format(from, "yyyy-MM-dd") : undefined;
      const toEnd = to ? format(to, "yyyy-MM-dd") : undefined;

      // Strip out undefined values
      const dateFilter = omitBy(
        {
          from: fromStart,
          to: toEnd,
          hasChange: true,
        },
        (v) => v === undefined
      );

      onChange(dateFilter);
    },
    [onChange]
  );

  /**
   * For now, the DateRangePicker value prop needs to be memoized because a hook checks
   * For === triple equality on value and if it doesn't equal itself, it resets the value.
   */

  const fromString = props.value?.from;
  const toString = props.value?.to;
  const value = useMemo(() => {
    const from = fromString && new Date(fromString);
    const to = toString && new Date(toString);

    return omitBy(
      {
        from,
        to,
      },
      (v) => v === undefined
    );
  }, [fromString, toString]);

  return (
    <DateRangePicker
      aria-label={t("aria-label")}
      closeText={t("close-text")}
      id={props.id}
      menuAriaLabel={t("menu-aria-label")}
      onChange={handleChange}
      options={dateOptions}
      placeholder={t("placeholder")}
      size={props.size}
      value={value}
    />
  );
};
